import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Button, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { FormattedDurationText, InputJob } from '~/commonComponents';
import { LeaveInterviewCreationPopup } from '~/commonComponents/popups/LeaveInterviewCreationPopup';
import { Field, Value } from '~/commonComponents/InputJob/Field';
import { useEnablePermanentJobMMFeature, useNavigationDetector } from '~/hooks';

import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { InputInviteCandidate } from '../../InviteCandidate/components';
import { OnsiteInterviewFormRules, TBreakTime, TOnsiteInterviewFormData } from './FormRule';
import { useFocusField } from './useFieldFocus';
import { SolidBriefcaseIcon } from '@tempmee/react-icons';

export enum OnsiteInterviewFormFields {
  AssistedHygiene = 'ASSISTED_HYGIENE',
  Dates = 'DATES',
  HourlyRate = 'HOURLY_RATE',
  Hours = 'HOURS',
  MealBreak = 'MEAL_BREAK',
  Specialty = 'SPECIALTY',
}

interface IOnsiteInterviewFormProps {
  jobId: string;
  jobTitle: string;
  office: string;
  profession: string;
  btnSecondaryText?: string;
  defaultValues?: TOnsiteInterviewFormData;
  isAfterJobPosting?: boolean;
  isEdit?: boolean;
  readonlyFields?: OnsiteInterviewFormFields[];
  unavailableDates?: Date[];
  btnSecondaryClick?: () => void;
  /**
   * Called whenever the form's `isDirty` flag flips. Lets the parent decide
   * which leave-confirmation popup to show (Unsaved Changes when dirty,
   * Wait Before You Go when clean) without duplicating the dirty tracking.
   */
  onIsDirtyChange?: (isDirty: boolean) => void;
  onSubmit(formData: TOnsiteInterviewFormData): void;
}

export const OnsiteInterviewForm = ({
  btnSecondaryText,
  defaultValues,
  isAfterJobPosting = false,
  isEdit = false,
  jobId,
  jobTitle,
  office,
  profession,
  readonlyFields = [],
  unavailableDates = [],
  btnSecondaryClick,
  onIsDirtyChange,
  onSubmit,
}: IOnsiteInterviewFormProps) => {
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
  } = useForm<TOnsiteInterviewFormData>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(OnsiteInterviewFormRules) as any,
  });

  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);

  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: isDirty,
    onNavigationAttempt: () => setShowUnsavedChangesPopup(true),
  });

  useEffect(() => {
    onIsDirtyChange?.(isDirty);
  }, [isDirty, onIsDirtyChange]);

  const handleFormSubmit = useCallback(
    (formData: TOnsiteInterviewFormData) => {
      allowNavigation();
      onSubmit(formData);
    },
    [allowNavigation, onSubmit],
  );

  const { isEnablePermanentJobMM } = useEnablePermanentJobMMFeature();

  const timeLimitInMinutes = RemoteConfigUtils.getOnsiteInterviewThresholdInMinutes();

  const { focus, handleFieldFocus } = useFocusField<OnsiteInterviewFormFields>();

  const readonly = new Set(readonlyFields);
  const watchBreakTime = useWatch({ control, name: 'breakTime' });
  const watchHoursArrival = useWatch({ control, name: 'hoursArrival' });
  const watchHoursDeparture = useWatch({ control, name: 'hoursDeparture' });
  const watchIsBreakPaid = useWatch({ control, name: 'isPaidBreakTime' });

  const handleSetHours = useCallback(
    (arrival: string, departure: string) => {
      setValue('hoursArrival', arrival, { shouldValidate: true, shouldDirty: true });
      setValue('hoursDeparture', departure, { shouldValidate: true, shouldDirty: true });
      handleFieldFocus(OnsiteInterviewFormFields.MealBreak);
    },
    [handleFieldFocus, setValue],
  );

  return (
    <Column gap={2} width="100%" className="mx-auto">
      <LeaveInterviewCreationPopup
        isOpen={showUnsavedChangesPopup}
        onDiscard={() => {
          setShowUnsavedChangesPopup(false);
          allowNavigation();
          navigateToPending();
        }}
        onKeepEditing={() => {
          setShowUnsavedChangesPopup(false);
          clearPendingNavigation();
        }}
      />

      <InputJob.SelectOffice isDisabled value={office} />

      <Field
        IconSrc={<SolidBriefcaseIcon size={24} color={colors.secondary[500]} />}
        isDisabled
        title="Job Title:"
        ValueComponent={() => <Value style={{ maxWidth: 'auto' }} text={jobTitle} />}
      />

      <Controller
        control={control}
        name="dates"
        render={({ field }) => (
          <InputJob.Date
            {...field}
            excluded={unavailableDates}
            isDisabled={readonly.has(OnsiteInterviewFormFields.Dates)}
            isOpen={focus === OnsiteInterviewFormFields.Dates}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.Dates)}
          />
        )}
      />

      <InputJob.Professional isDisabled value={profession} />

      <Controller
        control={control}
        name="specialty"
        render={({ field }) => (
          <InputJob.Specialty
            {...field}
            isDisabled={readonly.has(OnsiteInterviewFormFields.Specialty)}
            isOpen={focus === OnsiteInterviewFormFields.Specialty}
            professional={profession}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.Specialty)}
          />
        )}
      />

      <Controller
        control={control}
        name="isAssistedHygiene"
        render={({ field }) => (
          <InputJob.AssistedHygiene
            {...field}
            isDisabled={readonly.has(OnsiteInterviewFormFields.AssistedHygiene)}
            isOpen={focus === OnsiteInterviewFormFields.AssistedHygiene}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.AssistedHygiene)}
          />
        )}
      />

      <InputJob.Hours
        guidanceText={
          <Typography italic color={colors.neutral[500]}>
            Onsite interviews can run up to <FormattedDurationText duration={timeLimitInMinutes} />,{' '}
            giving you the time you need to evaluate skills and overall fit.
          </Typography>
        }
        isDisabled={readonly.has(OnsiteInterviewFormFields.Hours)}
        isOpen={focus === OnsiteInterviewFormFields.Hours}
        timeLimitInMinutes={timeLimitInMinutes}
        valueArrival={watchHoursArrival}
        valueDeparture={watchHoursDeparture}
        onChange={handleSetHours}
        onToggle={handleFieldFocus(OnsiteInterviewFormFields.Hours)}
      />

      <InputJob.MealBreak
        isDisabled={readonly.has(OnsiteInterviewFormFields.MealBreak)}
        isOpen={focus === OnsiteInterviewFormFields.MealBreak}
        valueBreak={watchBreakTime || undefined}
        valueIsPaid={watchIsBreakPaid}
        onToggle={handleFieldFocus(OnsiteInterviewFormFields.MealBreak)}
        onChange={(key, value) => {
          if (key === 'break')
            setValue('breakTime', value as TBreakTime, {
              shouldValidate: true,
              shouldDirty: true,
            });
          if (key === 'is_paid')
            setValue('isPaidBreakTime', value as boolean, {
              shouldValidate: true,
              shouldDirty: true,
            });
        }}
      />

      <Controller
        control={control}
        name="hourlyRate"
        render={({ field }) => (
          <InputJob.HourlyRate
            {...field}
            professional={profession}
            isOpen={focus === OnsiteInterviewFormFields.HourlyRate}
            onToggle={handleFieldFocus(OnsiteInterviewFormFields.HourlyRate)}
          />
        )}
      />

      {!isEdit && isEnablePermanentJobMM && !isAfterJobPosting && (
        <Controller
          control={control}
          name="professionals"
          render={({ field }) => <InputInviteCandidate {...field} jobPostingId={jobId} />}
        />
      )}

      <Row align="center" justify="center" gap={4} className="mt-8">
        {btnSecondaryClick && btnSecondaryText && (
          <Button variant="secondary" size="lg" onClick={btnSecondaryClick}>
            {btnSecondaryText}
          </Button>
        )}
        <Button
          disabled={isEdit ? !isDirty || !isValid : !isValid}
          size="lg"
          onClick={handleSubmit(handleFormSubmit as any)}
        >
          {isEdit ? 'Save Changes' : 'Next'}
        </Button>
      </Row>
    </Column>
  );
};
